import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
import { 
  MessageCircle, 
  Phone, 
  Mail, 
  Clock, 
  Send,
  User,
  HelpCircle,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  Home,
  Shield,
  FileText,
  CreditCard,
  BookOpen
} from 'lucide-react';

const Contact = () => {
  const { getSupportEmail, getSetting } = useSettings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    category: 'general'
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Get contact info from centralized settings
  const supportEmail = getSupportEmail() || 'support@manasti.com';
  const generalPhone = getSetting('general_contact_phone') || '01012345678';

  const categories = [
    { value: 'general', label: 'استفسار عام', icon: HelpCircle },
    { value: 'technical', label: 'دعم فني', icon: Shield },
    { value: 'payment', label: 'مشكلة دفع', icon: CreditCard },
    { value: 'academic', label: 'مساعدة أكاديمية', icon: BookOpen },
    { value: 'complaint', label: 'شكوى', icon: AlertCircle },
    { value: 'suggestion', label: 'اقتراح', icon: CheckCircle }
  ];

  const contactInfo = [
    {
      title: 'الدعم الفني',
      description: 'للمساعدة في المشاكل التقنية وحساباتك',
      icon: MessageCircle,
      contact: supportEmail,
      phone: generalPhone,
      hours: 'الأحد - الخميس: 9 ص - 9 م'
    },
    {
      title: 'الاستفسارات الأكاديمية',
      description: 'للمساعدة في المحتوى التعليمي والكورسات',
      icon: BookOpen,
      contact: supportEmail,
      phone: generalPhone,
      hours: 'الأحد - الخميس: 9 ص - 5 م'
    },
    {
      title: 'المبيعات والاشتراكات',
      description: 'للاستفسارات حول الاشتراكات والدفع',
      icon: CreditCard,
      contact: supportEmail,
      phone: generalPhone,
      hours: 'الأحد - الخميس: 9 ص - 6 م'
    }
  ];

  const faqs = [
    {
      question: 'كم يستغرق الرد على رسالتي؟',
      answer: 'نرد على جميع الرسائل خلال 24 ساعة عمل. في حالات الطوارئ، نرد خلال 4 ساعات.'
    },
    {
      question: 'هل يمكنني التواصل عبر الهاتف؟',
      answer: 'نعم، يمكنك التواصل معنا عبر الهاتف خلال ساعات العمل المحددة لكل قسم.'
    },
    {
      question: 'ما هي المعلومات المطلوبة للتواصل؟',
      answer: 'نحتاج فقط لاسمك الكامل، البريد الإلكتروني، ووصف واضح لمشكلتك أو استفسارك.'
    },
    {
      question: 'هل تقدمون دعماً على مدار الساعة؟',
      answer: 'الدعم الفني للطوارئ متاح 24/7، بينما الدعم العادي متخدم خلال ساعات العمل الرسمية.'
    }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        category: 'general'
      });
    }, 2000);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50/50 flex items-center justify-center">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h1 className="text-3xl font-black text-gray-900 mb-4">تم إرسال رسالتك بنجاح!</h1>
          <p className="text-gray-600 mb-8">
            شكراً لتواصلك معنا. سنعاود الاتصال بك في أقرب وقت ممكن.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-primary-600 text-white px-8 py-3 rounded-2xl font-black hover:bg-primary-700 transition-all"
          >
            العودة للصفحة الرئيسية
            <Home size={20} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="w-20 h-20 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-6 backdrop-blur-sm">
              <MessageCircle size={40} />
            </div>
            <h1 className="text-4xl font-black mb-4">تواصل معنا</h1>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              فريق الدعم متاح لمساعدتك في أي وقت. لا تتردد في التواصل معنا!
            </p>
            <Link
              to="/help"
              className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-2xl hover:bg-white/30 transition-all"
            >
              <ArrowLeft size={20} />
              العودة لمركز المساعدة
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Contact Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {contactInfo.map((info, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-primary-50 text-primary-600 rounded-xl flex items-center justify-center mb-4">
                <info.icon size={24} />
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-2">{info.title}</h3>
              <p className="text-gray-600 mb-4">{info.description}</p>
              <div className="space-y-2">
                <a href={`mailto:${info.contact}`} className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-black">
                  <Mail size={16} />
                  {info.contact}
                </a>
                <a href={`tel:${info.phone}`} className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-black">
                  <Phone size={16} />
                  {info.phone}
                </a>
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Clock size={16} />
                  {info.hours}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-black text-gray-900 mb-6">أرسل لنا رسالة</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-black text-gray-900 mb-2">
                    <User size={16} className="inline ml-2" />
                    الاسم الكامل
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                    placeholder="أدخل اسمك الكامل"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-black text-gray-900 mb-2">
                    <Mail size={16} className="inline ml-2" />
                    البريد الإلكتروني
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                    placeholder="example@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-black text-gray-900 mb-2">
                    <Phone size={16} className="inline ml-2" />
                    رقم الهاتف
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                    placeholder="01x xxxx xxxx"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-black text-gray-900 mb-2">
                    <HelpCircle size={16} className="inline ml-2" />
                    نوع الاستفسار
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-black text-gray-900 mb-2">
                  الموضوع
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                  placeholder="اكتب موضوع رسالتك"
                />
              </div>

              <div>
                <label className="block text-sm font-black text-gray-900 mb-2">
                  الرسالة
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all resize-none"
                  placeholder="اكتب رسالتك بالتفصيل..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary-600 text-white px-6 py-4 rounded-2xl font-black hover:bg-primary-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  'جاري الإرسال...'
                ) : (
                  <>
                    <Send size={20} />
                    إرسال الرسالة
                  </>
                )}
              </button>
            </form>
          </div>

          {/* FAQ Section */}
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-6">أسئلة شائعة</h2>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl border border-gray-100 p-6">
                  <h3 className="font-black text-gray-900 mb-2 flex items-start gap-2">
                    <HelpCircle size={18} className="text-primary-600 mt-1 flex-shrink-0" />
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>

            {/* Quick Links */}
            <div className="mt-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6">
              <h3 className="font-black text-gray-900 mb-4">روابط سريعة</h3>
              <div className="space-y-3">
                <Link
                  to="/help"
                  className="flex items-center gap-3 text-primary-600 hover:text-primary-700 font-black"
                >
                  <HelpCircle size={20} />
                  مركز المساعدة
                </Link>
                <Link
                  to="/terms"
                  className="flex items-center gap-3 text-primary-600 hover:text-primary-700 font-black"
                >
                  <FileText size={20} />
                  الشروط والأحكام
                </Link>
                <Link
                  to="/privacy"
                  className="flex items-center gap-3 text-primary-600 hover:text-primary-700 font-black"
                >
                  <Shield size={20} />
                  سياسة الخصوصية
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
